import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import RequestProgress from "./components/RequestProgress";
import LaunchModal from "./components/public/LaunchModal";
import ExitIntentModal from "./components/public/ExitIntentModal";
import FloatingBookNow from "./components/public/FloatingBookNow";
import { TelemetryTracker } from "./hooks/useTelemetry";
import HomePage from "./pages/public/HomePage";
import PrivacyPolicy from "./pages/public/PrivacyPolicy";
import TermsOfService from "./pages/public/TermsOfService";
import RefundPolicy from "./pages/public/RefundPolicy";
import Grievance from "./pages/public/Grievance";
import MedicalVaccinationConsent from "./pages/public/MedicalVaccinationConsent";
import VaccinationCatalogue from "./pages/public/VaccinationCatalogue";
import About from "./pages/public/About";
import ServicesPage from "./pages/public/ServicesPage";
import { ArticlesPage, ArticleDetailPage, CareersPage, SupportPage } from "./pages/public/ContentPages";
import CustomerLogin from "./pages/customer/CustomerLogin";
import { OnboardingPage, CustomerDashboard, BookingWizard, ProfilePage } from "./pages/customer/CustomerPortal";
import { HospitalDashboard } from "./pages/portals/RolePortals";
import NursePortal from "./pages/staff/NursePortal";
import DoctorPortal from "./pages/staff/DoctorPortal";
import OpsPortal from "./pages/staff/OpsPortal";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import Bookings from "./pages/admin/Bookings";
import Callbacks from "./pages/admin/Callbacks";
import LiveAppOps from "./pages/admin/LiveAppOps";
import Customers from "./pages/admin/Customers";
import FamilyMembers from "./pages/admin/FamilyMembers";
import Nurses from "./pages/admin/Nurses";
import NurseAttendance from "./pages/admin/NurseAttendance";
import Records from "./pages/admin/Records";
import Invoices from "./pages/admin/Invoices";
import InvoiceSettings from "./pages/admin/InvoiceSettings";
import Analytics from "./pages/admin/Analytics";
import Security from "./pages/admin/Security";
import CmsAdmin from "./pages/admin/CmsAdmin";
import SiteStatus from "./pages/admin/SiteStatus";
import { MaintenanceProvider } from "./lib/maintenance";
import MaintenanceGate from "./components/MaintenanceGate";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" />
    </div>
  );
  if (!user) return <Navigate to="/admin-login" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <RequestProgress />
      <BrowserRouter>
        <MaintenanceProvider>
          <MaintenanceGate>
            <LaunchModal>
              <TelemetryTracker />
              <ExitIntentModal />
              <FloatingBookNow />
              <Routes>
            {/* Public */}
            <Route path="/" element={<HomePage />} />
            <Route path="/download" element={<HomePage />} />
            <Route path="/contact" element={<HomePage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/refund" element={<RefundPolicy />} />
            <Route path="/grievance" element={<Grievance />} />
            <Route path="/medical-vaccination-consent" element={<MedicalVaccinationConsent />} />
            <Route path="/medical-declaration" element={<MedicalVaccinationConsent />} />
            <Route path="/vaccination-catalogue" element={<VaccinationCatalogue />} />
            <Route path="/vaccination" element={<VaccinationCatalogue />} />
            <Route path="/request-appointment" element={<Navigate to="/booking" replace />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/articles" element={<ArticlesPage />} />
            <Route path="/articles/:slug" element={<ArticleDetailPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/support" element={<SupportPage />} />

            {/* Customer / role portals */}
            <Route path="/login" element={<CustomerLogin />} />
            <Route path="/signup" element={<CustomerLogin />} />
            <Route path="/register" element={<CustomerLogin />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/dashboard" element={<CustomerDashboard />} />
            <Route path="/booking" element={<BookingWizard />} />
            <Route path="/booking/:type/:id" element={<BookingWizard />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/nurse" element={<NursePortal />} />
            <Route path="/doctor" element={<DoctorPortal />} />
            <Route path="/doctor-dashboard" element={<Navigate to="/doctor" replace />} />
            <Route path="/ops" element={<OpsPortal />} />
            <Route path="/hospital-dashboard" element={<HospitalDashboard />} />

            {/* Admin */}
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="bookings" element={<Bookings />} />
              <Route path="live-app-ops" element={<LiveAppOps />} />
              <Route path="callbacks" element={<Callbacks />} />
              <Route path="appointment-requests" element={<Navigate to="/admin/bookings" replace />} />
              <Route path="customers" element={<Customers />} />
              <Route path="family-members" element={<FamilyMembers />} />
              <Route path="nurses" element={<Nurses />} />
              <Route path="nurse-attendance" element={<NurseAttendance />} />
              <Route path="records" element={<Records />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="invoice-settings" element={<InvoiceSettings />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="security" element={<Security />} />
              <Route path="site-status" element={<SiteStatus />} />
              <Route path="cms" element={<CmsAdmin />} />
            </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </LaunchModal>
          </MaintenanceGate>
        </MaintenanceProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
